/**
 * The app's navigation vocabulary: which views exist, which admin
 * destinations exist, and how both map to URL paths.
 *
 * This lives in shared/ rather than app/ because features legitimately need
 * it -- AdminPage needs the admin sections, useLeadsData needs to know which
 * view is asking, and the boot skeleton needs to know which shape to draw
 * before any of the app has mounted.
 */

export type View = 'focus' | 'table' | 'dashboard' | 'map' | 'admin' | 'manage'

export const VIEWS: readonly View[] = ['focus', 'table', 'dashboard', 'map', 'admin', 'manage']

/** Real URL paths per view (no #hash). Focus is the root. */
export const VIEW_PATH: Record<View, string> = {
  focus: '/',
  table: '/table',
  dashboard: '/dashboard',
  map: '/map',
  admin: '/admin',
  manage: '/manage',
}

/** Admin's four side-rail destinations. */
export type AdminSection = 'people' | 'leads' | 'activity' | 'settings'

/** The in-page sub-tabs, across all sections. Unique app-wide, so a path
 *  segment identifies one surface without needing its section. */
export type AdminSubTab =
  | 'users' | 'roles'
  | 'split' | 'redistribute' | 'import' | 'do-not-contact' | 'test-leads'
  | 'per-person' | 'features' | 'audit'
  | 'sync' | 'salesforce' | 'pipeline' | 'branding'

/** How a sub-tab is gated: a permission, or admin-only for a surface that is a
 *  personal view preference rather than an org capability. */
export type AdminGate = { perm: string } | { adminOnly: true }

export interface AdminSubTabDef {
  key: AdminSubTab
  label: string
  gate: AdminGate
  /** Shown only when the API reports this capability (GET /api/config/capabilities). */
  needs?: 'v2Writes'
}

export interface AdminSectionDef {
  key: AdminSection
  label: string
  subs: readonly AdminSubTabDef[]
}

export const ADMIN_NAV: readonly AdminSectionDef[] = [
  {
    key: 'people',
    label: 'Personer',
    subs: [
      { key: 'users', label: 'Användare', gate: { perm: 'manage_users' } },
      { key: 'roles', label: 'Roller', gate: { perm: 'manage_roles' } },
    ],
  },
  {
    key: 'leads',
    label: 'Leads',
    subs: [
      { key: 'split', label: 'Leadfördelning', gate: { perm: 'manage_users' } },
      { key: 'redistribute', label: 'Omfördela', gate: { perm: 'manage_users' } },
      { key: 'import', label: 'Import', gate: { perm: 'import_leads' } },
      { key: 'do-not-contact', label: 'Kontakta ej', gate: { perm: 'manage_users' } },
      { key: 'test-leads', label: 'Testleads', gate: { perm: 'manage_users' } },
    ],
  },
  {
    key: 'activity',
    label: 'Aktivitet',
    subs: [
      { key: 'per-person', label: 'Per person', gate: { perm: 'view_usage' } },
      { key: 'features', label: 'Funktioner', gate: { perm: 'view_usage' } },
      { key: 'audit', label: 'Logg', gate: { perm: 'manage_users' } },
    ],
  },
  {
    key: 'settings',
    label: 'Inställningar',
    subs: [
      { key: 'sync', label: 'Datasynk', gate: { perm: 'manage_integrations' } },
      { key: 'salesforce', label: 'Salesforce', gate: { perm: 'manage_integrations' } },
      { key: 'pipeline', label: 'Pipeline', gate: { perm: 'manage_pipeline' }, needs: 'v2Writes' },
      { key: 'branding', label: 'Varumärke', gate: { adminOnly: true } },
    ],
  },
]

/** A single admin destination: which rail item, and which sub-tab inside it. */
export interface AdminDest {
  section: AdminSection
  sub: AdminSubTab
}

export const DEFAULT_ADMIN_SECTION: AdminSection = 'people'

export function sectionDef(section: AdminSection): AdminSectionDef {
  return ADMIN_NAV.find(s => s.key === section)!
}

/** The destination a section lands on when no sub-tab is named. */
export function defaultSubTab(section: AdminSection): AdminSubTab {
  return sectionDef(section).subs[0].key
}

export const DEFAULT_ADMIN_DEST: AdminDest = {
  section: DEFAULT_ADMIN_SECTION,
  sub: defaultSubTab(DEFAULT_ADMIN_SECTION),
}

/** The ten paths the admin area used to have, each pointing at where that
 *  surface lives now. Bookmarks and in-app links still use them. */
const LEGACY_ADMIN_PATHS: Record<string, AdminDest> = {
  users: { section: 'people', sub: 'users' },
  roles: { section: 'people', sub: 'roles' },
  leads: { section: 'leads', sub: 'split' },
  redistribute: { section: 'leads', sub: 'redistribute' },
  import: { section: 'leads', sub: 'import' },
  activity: { section: 'activity', sub: 'per-person' },
  usage: { section: 'activity', sub: 'features' },
  audit: { section: 'activity', sub: 'audit' },
  sync: { section: 'settings', sub: 'sync' },
  branding: { section: 'settings', sub: 'branding' },
}

/** Query parameter carrying the lead whose overlay is open, so a specific
 *  company can be linked to from any view. */
export const LEAD_PARAM = 'lead'

/**
 * How changing the open overlay touches browser history.
 *
 * Opening one pushes, so Back closes it and stays in the view underneath.
 * Switching lead and closing replace, so the overlay's entry becomes the plain
 * view again: Back after closing lands on that same view (Tabell stays
 * Tabell) instead of skipping to whatever came before it, and opening several
 * leads in a row does not stack an entry per lead.
 */
export function overlayHistory(openId: string | null, nextId: string | null): 'push' | 'replace' {
  return !openId && nextId ? 'push' : 'replace'
}

/** Query parameter naming whose Focus queue an admin is viewing (the Kö
 *  select), so a reload stays on that seller's queue. Absent means your own. */
export const QUEUE_PARAM = 'queue'

/** QUEUE_PARAM's value for the admin test queue: every QA test lead
 *  (YCRM-93), whoever owns it. No email can equal it. */
export const TEST_QUEUE = 'test'

/** Query parameter carrying the lead Focus has open, so a reload keeps the
 *  place in the queue. A lead id rather than an index: the queue re-sorts. */
export const FOCUS_LEAD_PARAM = 'current'

/** The search string a view switch carries over. Only the queue: it is what the
 *  banner select shows on every view, while the open lead belongs to Focus and
 *  the overlay to the view it was opened from. */
export function carriedSearch(search: string): string {
  const queue = new URLSearchParams(search).get(QUEUE_PARAM)
  return queue ? `?${new URLSearchParams({ [QUEUE_PARAM]: queue })}` : ''
}

function normalizePath(p: string): string {
  const s = p.replace(/\/+$/, '').toLowerCase()
  return s === '' ? '/' : s
}

function asSection(seg: string | undefined): AdminSection | null {
  return ADMIN_NAV.find(s => s.key === seg)?.key ?? null
}

/** The view a path belongs to, or null if the path names no view.
 *
 * Strict on purpose. /admin/activity/features is the admin view, but
 * /admin/login is not a view at all -- it is where the API drops you after
 * Google sign-in, and treating it as "admin" would land a non-admin on an
 * "Admins only" screen instead of the view they were last using. Anything
 * unrecognised returns null so the caller can send it somewhere real.
 */
export function viewFromPath(pathname: string): View | null {
  const path = normalizePath(pathname)
  if (path === '/') return 'focus'
  const [, first, second, third, ...rest] = path.split('/')
  if (rest.length) return null
  const hit = VIEWS.find(v => v !== 'focus' && VIEW_PATH[v] === `/${first}`)
  if (!hit) return null
  if (hit === 'manage') return !second || (!third && MANAGE_OVERVIEW_PATHS.has(second)) ? 'manage' : null
  if (hit !== 'admin') return second ? null : hit
  if (!second) return 'admin'
  const section = asSection(second)
  if (!section) return third || !LEGACY_ADMIN_PATHS[second] ? null : 'admin'
  if (third && !sectionDef(section).subs.some(s => s.key === third)) return null
  return 'admin'
}

/** Hantera sub-paths that resolve to Hantera and land on its overview.
 *  Hantera's Salesforce section is hidden for now (YCRM-68); Admin > Salesforce
 *  keeps the connection settings and status refresh. */
const MANAGE_OVERVIEW_PATHS = new Set(['salesforce'])

/** The bare Hantera path for a Hantera sub-path, or null when the path is
 *  already bare or is not Hantera's. */
export function manageRedirectFor(pathname: string): string | null {
  const path = normalizePath(pathname)
  return path !== VIEW_PATH.manage && viewFromPath(path) === 'manage' ? VIEW_PATH.manage : null
}

/** Which admin destination a path asks for, falling back rather than
 *  rendering nothing. Legacy single-segment paths resolve to their new home. */
export function adminDestFromPath(pathname: string): AdminDest {
  const [, , second, third] = normalizePath(pathname).split('/')
  const section = asSection(second)
  if (!section) return LEGACY_ADMIN_PATHS[second ?? ''] ?? DEFAULT_ADMIN_DEST
  const sub = sectionDef(section).subs.find(s => s.key === third)?.key
  return { section, sub: sub ?? defaultSubTab(section) }
}

/** The canonical path for a path that still uses the old vocabulary, or null
 *  when the path is already canonical. */
export function adminRedirectFor(pathname: string): string | null {
  const path = normalizePath(pathname)
  const [, first, second, third] = path.split('/')
  if (`/${first}` !== VIEW_PATH.admin) return null
  if (third || asSection(second)) return null
  const dest = LEGACY_ADMIN_PATHS[second ?? '']
  if (!dest) return null
  const canonical = adminPath(dest)
  return canonical === path ? null : canonical
}

/** A section's default sub-tab is addressed by the bare section path, so the
 *  first thing an admin sees has the shortest URL. */
export function adminPath(dest: AdminDest = DEFAULT_ADMIN_DEST): string {
  const base = `${VIEW_PATH.admin}/${dest.section}`
  return dest.sub === defaultSubTab(dest.section) ? base : `${base}/${dest.sub}`
}

export function pathFor(view: View, dest?: AdminDest): string {
  if (view === 'admin') return adminPath(dest)
  return VIEW_PATH[view]
}
